import { t } from '../../i18n.es.js';
import React, { useState, useEffect } from 'react';
import { styled, Tag, SvgIcon, Button, Input, LazyImage } from '@teko/ui';
import Link from 'next/link';
import { getTrackingData } from '@teko/tracking';
import { ProductGifts } from '../ProductGifts/ProductGifts.es.js';
import { ProductName } from '../ProductName/ProductName.es.js';
import { FlashSaleBadge } from '../FlashSaleBadge/FlashSaleBadge.es.js';
import { ProductPrice } from '../ProductPrice/ProductPrice.es.js';
import { Loading } from '../Loading/Loading.es.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const DisplayStatus = {
  NONE: 1,
  CONTACT: 2,
  BUYABLE: 3
};
const StyledProductCard = styled.div`
  padding: 0.3rem;
  background: #fff;
  cursor: pointer;
  display: block;
  text-decoration: none;
  color: #333;
  border-radius: 0.5rem;
  height: 100%;
`;
const StyledProductGifts = styled(ProductGifts)`
  margin-top: 0.5rem;
`;
const StyledProductName = styled(ProductName)`
  margin-top: 0.5rem;
`;
const StyledTag = styled(Tag)`
  margin-right: 0.2rem;
  margin-bottom: 0.2rem;
  font-size: 0.65rem;
`;
const ImageWrapper = styled.div`
  position: relative;
`;
const Tags = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  display: flex;
  flex-wrap: wrap;
`;
const FrameWrapper = styled.div(({ frame }) => `
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  display: flex;
  flex-wrap: wrap;
  background-image: url(${frame});
  background-size: cover;
  height: 100%;
  width: 100%;
  `);
const PriceWrapper = styled.div`
  display: flex;
  margin-top: 0.5rem;
`;
const Icons = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-left: 0.2rem;
`;
const StyledIcon = styled(SvgIcon)`
  margin-left: 0.5rem;
`;
const StyledFlashSaleBadge = styled(FlashSaleBadge)`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  border-radius: 0;
`;
const Hover = styled.div`
  padding: 0.7rem;
  border-radius: 0.5rem;
  transition: transform 0.2s;
  -webkit-font-smoothing: subpixel-antialiased;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  &:hover {
    transform: scale(1.05);
  }
`;
const CustomLink = styled.a`
  color: unset;
  text-decoration: unset;
`;
const Contact = styled.div(({ theme }) => `
  background-color: ${theme == null ? void 0 : theme.brand.tertiary};
  color: ${theme == null ? void 0 : theme.brand.primary};
  padding: 2px 8px;
  font-size: 0.75rem;
  border-radius: 0.25rem;
  width: fit-content;
  font-weight: 500;
  margin-top: 0.5rem;
`);
const AddToCartBtn = styled(Button)`
  font-weight: 500;
`;
const QuantityStepper = styled.div(({ theme }) => `
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid ${theme == null ? void 0 : theme.brand.primary};
  height: 40px;
  border-radius: 4px;
  overflow: hidden;
  `);
const StyledInput = styled(Input)(({ theme }) => `
    padding: 0;
    border: none;
    width: 100%;
    text-align: center;
    color: ${theme == null ? void 0 : theme.brand.primary};
  `);
const StyledButton = styled(Button)`
  border: none;
  height: 40px;
`;
const AddToCartWrapper = styled.div`
  margin-top: 1rem;
`;
const StyledTagStatus = styled(Tag)`
  font-size: 11px;
  border-radius: 4px;
  position: absolute;
  bottom: 0;
  left: 0;
`;
const ProductCard = (_a) => {
  var _b = _a, {
    tags = [],
    image,
    title,
    price,
    link,
    icons = [],
    coupon,
    gifts,
    flashsale,
    money,
    regionName,
    className,
    totalAvailableByStocks,
    displayStatus,
    addToCart,
    needManageStock,
    frame,
    contactLink,
    tag,
    showContact = false
  } = _b, props = __objRest(_b, [
    "tags",
    "image",
    "title",
    "price",
    "link",
    "icons",
    "coupon",
    "gifts",
    "flashsale",
    "money",
    "regionName",
    "className",
    "totalAvailableByStocks",
    "displayStatus",
    "addToCart",
    "needManageStock",
    "frame",
    "contactLink",
    "tag",
    "showContact"
  ]);
  const [qty, setQty] = useState(() => addToCart && addToCart.quantityInCart || 0);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (addToCart && !loading)
      setQty(addToCart.quantityInCart || 0);
  }, [addToCart, loading]);
  const handleUpdateQty = async () => {
    if (qty && qty !== (addToCart == null ? void 0 : addToCart.quantityInCart)) {
      setLoading(true);
      await (addToCart == null ? void 0 : addToCart.onChangeQuantity(qty));
      setLoading(false);
    } else {
      setQty((addToCart == null ? void 0 : addToCart.quantityInCart) || 0);
    }
  };
  const node = /* @__PURE__ */ React.createElement(Hover, {
    className: "product-card__content"
  }, /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(ImageWrapper, null, frame && /* @__PURE__ */ React.createElement(FrameWrapper, {
    frame
  }), /* @__PURE__ */ React.createElement(LazyImage, __spreadProps(__spreadValues({
    imageWidth: 320,
    borderRadius: 8,
    contain: true
  }, image), {
    aspectRatio: 1
  })), flashsale && /* @__PURE__ */ React.createElement(StyledFlashSaleBadge, {
    size: "md",
    endAt: flashsale.endAt,
    sold: flashsale.value,
    total: flashsale.total,
    stock: totalAvailableByStocks
  }), !!tags.length && !flashsale && /* @__PURE__ */ React.createElement(Tags, null, tags.map((tag2, index) => /* @__PURE__ */ React.createElement(StyledTag, {
    key: index,
    outline: true
  }, tag2))), !flashsale && tag && /* @__PURE__ */ React.createElement(StyledTagStatus, __spreadValues({}, tag))), /* @__PURE__ */ React.createElement("div", {
    className: "product-card__info"
  }, /* @__PURE__ */ React.createElement(StyledProductName, {
    needManageStock,
    totalAvailableByStocks,
    name: title.name,
    isFlashSelling: flashsale !== null
  }), totalAvailableByStocks > 0 || displayStatus !== DisplayStatus.CONTACT ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(PriceWrapper, null, /* @__PURE__ */ React.createElement(ProductPrice, {
    price: price.price,
    listedPrice: price.listedPrice,
    discount: price.discount
  }), /* @__PURE__ */ React.createElement(Icons, null, icons.map((icon, index) => /* @__PURE__ */ React.createElement(StyledIcon, __spreadValues({
    key: index
  }, icon))))), gifts && /* @__PURE__ */ React.createElement(StyledProductGifts, {
    label: t("QU\xC0 T\u1EB6NG"),
    images: gifts.images
  })) : /* @__PURE__ */ React.createElement(React.Fragment, null, displayStatus === DisplayStatus.CONTACT ? /* @__PURE__ */ React.createElement(Contact, null, t("LI\xCAN H\u1EC6")) : /* @__PURE__ */ React.createElement(Tag, {
    outline: true
  }, displayStatus)))), addToCart && /* @__PURE__ */ React.createElement(AddToCartWrapper, null, !!addToCart.quantityInCart ? /* @__PURE__ */ React.createElement(QuantityStepper, null, /* @__PURE__ */ React.createElement(Loading, {
    isLoading: loading
  }, /* @__PURE__ */ React.createElement(StyledButton, {
    onClick: (e) => {
      e.preventDefault();
      addToCart.onDecrease();
    },
    outline: true,
    block: true,
    variant: "primary"
  }, /* @__PURE__ */ React.createElement(SvgIcon, {
    name: "subtract",
    themeColor: "primary",
    size: 22
  })), /* @__PURE__ */ React.createElement(StyledInput, {
    value: qty || "",
    onClick: (e) => e.preventDefault(),
    onChange: (e) => setQty(Number(e.target.value)),
    type: "number",
    onBlur: handleUpdateQty,
    onKeyDown: (e) => {
      if (e.key === "Enter")
        handleUpdateQty();
    }
  }), /* @__PURE__ */ React.createElement(StyledButton, {
    onClick: (e) => {
      e.preventDefault();
      addToCart.onIncrease();
    },
    outline: true,
    block: true,
    variant: "primary"
  }, /* @__PURE__ */ React.createElement(SvgIcon, {
    name: "plus",
    themeColor: "primary",
    size: 22
  })))) : /* @__PURE__ */ React.createElement(AddToCartBtn, {
    onClick: (e) => {
      e.preventDefault();
      if (showContact) {
        if (contactLink)
          window.open(contactLink);
        return;
      }
      addToCart.onAddToCart();
    },
    size: "lg",
    disabled: !!(showContact && !contactLink),
    variant: showContact && !contactLink ? "disabled" : "primary",
    outline: true,
    block: true
  }, showContact ? contactLink ? t("Li\xEAn h\u1EC7") : t("H\u1EBFt h\xE0ng") : t("Th\xEAm v\xE0o gi\u1ECF"))));
  if (link) {
    return /* @__PURE__ */ React.createElement(Link, __spreadProps(__spreadValues({}, link), {
      legacyBehavior: true
    }), /* @__PURE__ */ React.createElement(CustomLink, __spreadProps(__spreadValues({}, props), {
      className: className || ""
    }), /* @__PURE__ */ React.createElement(StyledProductCard, __spreadValues({
      className: "product-card"
    }, regionName && getTrackingData({
      isTrackContent: true,
      regionName,
      contentName: props.sku,
      target: "productDetail"
    })), node)));
  }
  return /* @__PURE__ */ React.createElement(StyledProductCard, __spreadValues(__spreadProps(__spreadValues({}, props), {
    className: `${className || ""} product-card`
  }), regionName && getTrackingData({
    isTrackContent: true,
    regionName,
    contentName: props.sku,
    target: "productDetail"
  })), node);
};

export { DisplayStatus, ProductCard };
